import React from 'react';
import styled, { css } from 'styled-components';

import { useMedia } from '@tager/web-core';

import { ContentContainerSmall } from '@/components/ContentContainer';
import { useTypedSelector } from '@/store/store';
import {
  selectPostsListPosts,
  selectPostsListPostsStatus,
  selectPostsListSeo,
  selectPostsListTags,
} from '@/store/reducers/posts-list';
import PostCard from '@/components/PostCard';
import { media } from '@/utils/mixin';
import { BlogCategory } from '@/typings/model';

import FiltersByTags from './components/FiltersByTags';
import { convertText } from './helpers';

type Props = {
  category: BlogCategory;
};

function PostsList({ category }: Props) {
  const mainTitle = useTypedSelector(selectPostsListSeo)?.name;
  const tags = useTypedSelector(selectPostsListTags);
  const posts = useTypedSelector(selectPostsListPosts);
  const postsStatus = useTypedSelector(selectPostsListPostsStatus);
  const postsItemsLength = posts?.items.length;
  const isTablet = useMedia('(max-width: 1259px)');

  return (
    <Component>
      <ContentContainerSmall>
        <Content>
          <Left>
            <Header>
              <MainTitle>{mainTitle}</MainTitle>
              {isTablet && (
                <FiltersByTags
                  tags={tags}
                  category={category}
                  resultText={convertText(category, postsItemsLength)}
                />
              )}
            </Header>

            <Posts isLoading={postsStatus === 'LOADING'}>
              {posts &&
                posts.items.length > 0 &&
                posts.items.map(({ title, image, url }, index) => (
                  <PostCard
                    key={index}
                    image={image}
                    title={title ?? ''}
                    url={url ?? ''}
                  />
                ))}
            </Posts>
          </Left>
          <Right>
            <FiltersByTags
              tags={tags}
              category={category}
              resultText={convertText(category, postsItemsLength)}
            />
          </Right>
        </Content>
      </ContentContainerSmall>
    </Component>
  );
}

export default PostsList;

const Component = styled.section`
  padding: 60px 0;
  background: ${(props) => props.theme.white};

  ${media.tablet(css`
    padding: 40px 0 60px;
  `)}

  ${media.mobile(css`
    padding: 20px 0 60px;
  `)}

  ${ContentContainerSmall} {
    padding: 0 40px;

    ${media.tablet(css`
      padding: 0 20px;
    `)}
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 820px;
  max-width: 820px;

  ${media.tablet(css`
    flex: auto;
    max-width: none;
  `)}
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 308px;
  max-width: 308px;

  ${media.tablet(css`
    display: none;
  `)}
`;

const Header = styled.div`
  ${media.tablet(css`
    display: flex;
    flex-direction: column;
  `)}
`;

const MainTitle = styled.h1`
  font-weight: 700;
  font-size: 40px;
  line-height: 52px;
  margin: 0;
  color: ${(props) => props.theme.black};

  ${media.tablet(css`
    font-size: 24px;
    line-height: 32px;
  `)}
`;

const Posts = styled.div<{ isLoading: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 60px 30px;
  margin-top: 30px;

  ${media.tablet(css`
    margin-top: 20px;
    gap: 30px 20px;
  `)}

  ${media.mobile(css`
    grid-template-columns: repeat(1, 1fr);
  `)}

  ${({ isLoading }) =>
    isLoading &&
    css`
      opacity: 0.5;
    `}
`;
